import {useAppSelector} from '@/redux-store/store';
import {Delete, PhotoCamera} from '@mui/icons-material';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Box
} from '@mui/material';
import {Form, Formik} from 'formik';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import CompanyValidationSchema, {
  EMAIL_MAX_LENGTH,
  URL_MAX_LENGTH
} from '@/schemas/company';
import {TITLE_MAX_LENGTH} from '@/schemas/company';
import {WELCOME_MAX_LENGTH} from '@/schemas/company';
import {IMPRINT_MAX_LENGTH} from '@/schemas/company';
import {useDispatch} from 'react-redux';
import {Company} from '@/types/company';
import {
  AdminCompaniesFormValues,
  initialState,
  resetCompaniesCreateFormData,
  setCompaniesCreateFormData
} from '@/redux-store/slices/admin/companies';
import {addCompany} from '@/rest/admin/companies/add-company';
import {SupportedLanguage} from '@/types/common';
import {CompanyRoleAll} from '@/constants';
import TypographyTitle from '@/components/ui/typography-title';
import {SportSelectInput} from '@/components/forms/input/sports';

interface CompanyAddFormProps {
  onCancel?: () => void;
  onSubmit?: (updatedData: Company) => void;
  onError?: (error: Error) => void;
}

const CompanyAddForm: React.FC<CompanyAddFormProps> = ({
  onCancel,
  onError,
  onSubmit
}) => {
  const initialValues = useAppSelector(
    (state) => state.adminCompanies.adminCompaniesCompanyCreateForm
  );
  const {t} = useTranslation();

  const [fileSizeError, setFileSizeError] = useState<string | null>(null);
  const [imprintLengthDE, setImprintLengthDE] = useState(
    initialValues.imprint.labels.de.length
  );
  const [imprintLengthEN, setImprintLengthEN] = useState(
    initialValues.imprint.labels.en.length
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const dispatch = useDispatch();

  const ValidationSchema = CompanyValidationSchema(t);

  const allRoles = Object.values(CompanyRoleAll);

  const handleLogoChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const fileSize = file.size / 1024 / 1024;
        if (fileSize > 0.5) {
          setFileSizeError(t('file_size_error', {maxSize: 500, unit: 'kB'}));
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          setFileSizeError(null);
          dispatch(
            setCompaniesCreateFormData({
              logo: reader.result as string
            })
          );
        };
      }
    },
    [dispatch, t]
  );

  const resetFormData = useCallback(() => {
    dispatch(resetCompaniesCreateFormData());
    setLogoError(null);
    setSubmitted(false);
    setFileSizeError(null);
  }, [dispatch]);

  const handleDeleteLogo = useCallback(() => {
    setFileSizeError(null);
    dispatch(
      setCompaniesCreateFormData({
        logo: ''
      })
    );
  }, [dispatch]);

  const submitForm = useCallback(
    (values: AdminCompaniesFormValues) => {
      setIsSubmitting(true);
      const {title, welcome, email, imprint, allowedSports} = values;

      const companiesObject = {
        title,
        email,
        welcome,
        imprint,
        roles: allRoles,
        allowedSports,
        logo: initialValues.logo
      };

      addCompany(companiesObject)
        .then((response) => {
          setIsSubmitting(false);
          if (response) {
            if (onSubmit) {
              onSubmit(response);
              resetFormData();
            }
          } else {
            if (onError) {
              onError(new Error('Error while adding company'));
            }
          }
        })
        .catch((error) => {
          setIsSubmitting(false);
          console.error('Error:', error);
          if (onError) {
            onError(error as Error);
          }
        });
    },
    [allRoles, onError, onSubmit, resetFormData, initialValues]
  );

  useEffect(() => {
    if (!initialValues.logo) {
      setLogoError(t('validation:is_required', {name: t('company_logo')}));
    } else {
      setLogoError(null);
    }
  }, [initialValues.logo, t]);

  useEffect(() => {
    if (initialValues) {
      setImprintLengthDE(initialValues.imprint.labels.de.length);
      setImprintLengthEN(initialValues.imprint.labels.en.length);
    }
  }, [initialValues, initialValues.labelLanguage]);

  return (
    <>
      <Formik
        //enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={(values) => {
          if (logoError || fileSizeError) {
            setSubmitted(true);
            setIsSubmitting(false);
            return;
          }
          submitForm(values);
          dispatch(setCompaniesCreateFormData(values));
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          resetForm,
          validateForm,
          setFieldValue
        }) => {
          return (
            <Form>
              <TypographyTitle textKey="add_company" />
              <Grid container spacing={2} my={1} marginTop={3}>
                <Grid item xs={12}>
                  <TextField
                    name="title"
                    value={values.title}
                    label={t('title')}
                    onChange={(e) => {
                      handleChange(e);
                      dispatch(
                        setCompaniesCreateFormData({
                          title: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                  <FormHelperText>
                    {values.title.length} / {TITLE_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={1} marginTop={3}>
                <Grid item xs={12}>
                  <TextField
                    name="email"
                    value={values.email}
                    label={t('email')}
                    onChange={(e) => {
                      handleChange(e);

                      dispatch(
                        setCompaniesCreateFormData({
                          email: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                  <FormHelperText>
                    {values.email.length} / {EMAIL_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={2} marginTop={3}>
                <Grid item xs={12}>
                  <TextField
                    name="welcome"
                    value={values.welcome}
                    label={t('welcome_text')}
                    onChange={(e) => {
                      handleChange(e);

                      dispatch(
                        setCompaniesCreateFormData({
                          welcome: e.target.value
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    multiline
                    rows={4}
                    error={touched.welcome && Boolean(errors.welcome)}
                    helperText={touched.welcome && errors.welcome}
                  />
                  <FormHelperText>
                    {values.welcome.length} / {WELCOME_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
              </Grid>

              <Grid container spacing={0} marginTop={2}>
                <Grid
                  item
                  xs={12}
                  container
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography
                    variant="subtitle1"
                    marginTop={1}
                    marginBottom={1}
                  >
                    {t('imprint')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="imprint.url"
                    value={values.imprint.url}
                    label={t('url')}
                    onChange={(e) => {
                      handleChange(e);

                      dispatch(
                        setCompaniesCreateFormData({
                          imprint: {
                            ...values.imprint,
                            url: e.target.value
                          }
                        })
                      );
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    error={touched.imprint?.url && Boolean(errors.imprint?.url)}
                    helperText={touched.imprint?.url && errors.imprint?.url}
                  />
                  <FormHelperText>
                    {values.imprint.url.length} / {URL_MAX_LENGTH}
                  </FormHelperText>
                </Grid>
                <Grid item xs={12} marginTop={3}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      name="labelLanguage"
                      value={values.labelLanguage}
                      onChange={(e) => {
                        const newLanguage = e.target.value as SupportedLanguage;
                        setFieldValue('labelLanguage', newLanguage);
                        dispatch(
                          setCompaniesCreateFormData({
                            labelLanguage: newLanguage
                          })
                        );
                        const length =
                          values.imprint.labels[e.target.value as 'de' | 'en']
                            .length;

                        if (e.target.value === 'de') {
                          setImprintLengthDE(length);
                        } else if (e.target.value === 'en') {
                          setImprintLengthEN(length);
                        }
                      }}
                      style={{marginLeft: '10px'}}
                    >
                      <FormControlLabel
                        value="de"
                        control={<Radio />}
                        label={t('german')}
                      />
                      <FormControlLabel
                        value="en"
                        control={<Radio />}
                        label={t('english')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} marginTop={0}>
                  <TextField
                    name={`imprint.labels.${values.labelLanguage}`}
                    value={values.imprint.labels[values.labelLanguage]}
                    label={t('label_company')}
                    onChange={(e) => {
                      handleChange(e);

                      dispatch(
                        setCompaniesCreateFormData({
                          imprint: {
                            ...values.imprint,
                            labels: {
                              ...values.imprint.labels,
                              [values.labelLanguage]: e.target.value
                            }
                          }
                        })
                      );
                      if (values.labelLanguage === 'de') {
                        setImprintLengthDE(e.target.value.length);
                      } else if (values.labelLanguage === 'en') {
                        setImprintLengthEN(e.target.value.length);
                      }
                    }}
                    onBlur={handleBlur}
                    fullWidth
                    multiline
                    maxRows={2}
                  />
                  <FormHelperText>
                    {values.labelLanguage === 'de'
                      ? `${imprintLengthDE} / ${IMPRINT_MAX_LENGTH}`
                      : `${imprintLengthEN} / ${IMPRINT_MAX_LENGTH}`}
                  </FormHelperText>
                  <FormHelperText error>
                    {touched.imprint?.labels?.de && errors.imprint?.labels?.de}
                  </FormHelperText>
                  <FormHelperText error>
                    {touched.imprint?.labels?.en && errors.imprint?.labels?.en}
                  </FormHelperText>
                </Grid>
                <Grid container spacing={2} my={1} marginTop={2}>
                  <Grid item xs={12}>
                    <SportSelectInput
                      name="allowedSports"
                      label="company_sports"
                      value={values.allowedSports}
                      error={errors.allowedSports}
                      touched={touched.allowedSports}
                      onBlur={handleBlur}
                      onChange={(sportIds) => {
                        let sports;
                        if (typeof sportIds === 'string') {
                          sports = [sportIds];
                        } else {
                          sports = sportIds;
                        }
                        setFieldValue('allowedSports', sports);
                        dispatch(
                          setCompaniesCreateFormData({
                            allowedSports: sports
                          })
                        );
                      }}
                      multiple={true}
                      optional
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container spacing={2} my={3} justifyContent={'center'}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{display: 'flex', justifyContent: 'center'}}
                >
                  <Button
                    component="span"
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteLogo}
                    sx={{
                      display: {xs: 'none', sm: 'flex'},
                      textTransform: 'none'
                    }}
                  >
                    {t('delete_logo')}
                  </Button>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <label htmlFor="logo-upload">
                    <Button
                      component="span"
                      variant="outlined"
                      color="secondary"
                      startIcon={<PhotoCamera />}
                      sx={{
                        display: {xs: 'none', sm: 'flex'},
                        textTransform: 'none'
                      }}
                    >
                      {t('logo_upload')}
                    </Button>
                  </label>
                  <input
                    id="logo-upload"
                    name="logo"
                    type="file"
                    onChange={handleLogoChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginRight: 2,
                    marginBottom: 1
                  }}
                >
                  <label htmlFor="logo-delete-xs">
                    <IconButton
                      aria-label={t('delete_logo')}
                      onClick={handleDeleteLogo}
                      color="error"
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'}
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </label>
                  <label htmlFor="logo-upload-xs">
                    <IconButton
                      aria-label={t('logo_upload')}
                      color="secondary"
                      component="span"
                      sx={{
                        display: {xs: 'flex', sm: 'none'},
                        marginLeft: 4,
                        marginTop: '1px'
                      }}
                    >
                      <PhotoCamera />
                    </IconButton>
                  </label>
                  <input
                    id="logo-upload-xs"
                    name="logo"
                    type="file"
                    onChange={handleLogoChange}
                    accept=".jpg, .jpeg, .png"
                    hidden
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginRight: 2,
                    marginBottom: 1
                  }}
                >
                  {initialValues.logo && (
                    <Box
                      component="img"
                      src={initialValues.logo}
                      alt={t('company_logo')}
                      sx={{
                        maxWidth: {xs: '150px', sm: '200px'},
                        maxHeight: '100px',
                        marginTop: {xs: 2, sm: 0}
                      }}
                    />
                  )}
                </Grid>
              </Grid>

              {fileSizeError && (
                <Typography align="center" color="error" marginTop={2}>
                  {fileSizeError}
                </Typography>
              )}
              {submitted && logoError && (
                <Typography align="center" color="error" marginTop={2}>
                  {logoError}
                </Typography>
              )}

              {!fileSizeError && (
                <FormHelperText
                  error
                  sx={{
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  {errors.logo && touched.logo && errors.logo}
                </FormHelperText>
              )}

              <Grid
                container
                direction={{xs: 'column-reverse', sm: 'row'}}
                justifyContent="center"
                spacing={2}
                sx={{
                  marginTop: {xs: 2, sm: 5},
                  marginBottom: 5
                }}
              >
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',

                    order: {xs: 1, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      dispatch(resetCompaniesCreateFormData());
                      resetForm({
                        values: initialState.adminCompaniesCompanyCreateForm
                      });
                      if (onCancel) {
                        onCancel();
                      }
                    }}
                    sx={{
                      width: {xs: '50%', sm: '70%'}
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    order: {xs: 2, sm: 2},
                    marginBottom: {xs: 2, sm: 0}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      resetFormData();
                      resetForm({
                        values: initialState.adminCompaniesCompanyCreateForm
                      });
                      setFieldValue('labelLanguage', 'de');
                      validateForm();
                    }}
                    sx={{width: {xs: '50%', sm: '70%'}}}
                  >
                    {t('reset')}
                  </Button>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={4}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: {xs: 2, sm: 0},
                    order: {xs: 3, sm: 3}
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    onClick={() => {
                      setTimeout(() => {
                        setSubmitted(true);
                      }, 500);
                    }}
                    disabled={isSubmitting}
                    sx={{width: {xs: '50%', sm: '70%'}}}
                  >
                    {t('create')}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default CompanyAddForm;
